/**
 * 备注输入组件
 * 用于工作流提交时添加用户备注
 */

import type { ReactElement, ChangeEvent } from 'react'
import { MessageSquare } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * 备注输入组件属性
 */
interface RemarkInputProps {
  /** 当前值 */
  value: string
  /** 值变更回调 */
  onChange: (value: string) => void
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义类名 */
  className?: string
}

/** 最大字符数限制 */
const MAX_LENGTH = 100

/**
 * 备注输入组件
 * @param props - 组件属性
 * @returns 备注输入 ReactElement
 */
export function RemarkInput({
  value,
  onChange,
  disabled = false,
  className,
}: RemarkInputProps): ReactElement {
  /**
   * 处理输入变化
   * @param e - 输入事件
   */
  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const newValue = e.target.value
    if (newValue.length <= MAX_LENGTH) {
      onChange(newValue)
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="remark" className="text-sm font-medium">
            备注
          </Label>
        </div>
        <span className={cn(
          'text-xs text-muted-foreground',
          value.length >= MAX_LENGTH && 'text-destructive'
        )}>
          {value.length}/{MAX_LENGTH}
        </span>
      </div>
      <Input
        id="remark"
        placeholder="添加备注，方便识别任务（可选）"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        maxLength={MAX_LENGTH}
      />
    </div>
  )
}
